import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  LogOut, Users, Newspaper, Video, FileText, Image as ImageIcon, MessageSquare,
  Sun, Moon, KeyRound, ArrowLeft, Briefcase, Target, Layers,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { BRAND } from '../../data/site';
import { makeAdminClient } from '../../lib/api';

import TeamPanel from './panels/TeamPanel';
import VenturesPanel from './panels/VenturesPanel';
import PillarsPanel from './panels/PillarsPanel';
import ClustersPanel from './panels/ClustersPanel';
import NewsPanel from './panels/NewsPanel';
import MediaPanel from './panels/MediaPanel';
import CasePanel from './panels/CasePanel';
import HeroPanel from './panels/HeroPanel';
import InquiriesPanel from './panels/InquiriesPanel';
import PasswordPanel from './panels/PasswordPanel';

const TABS = [
  { key: 'team', label: 'Team & Advisory', icon: Users, Panel: TeamPanel },
  { key: 'ventures', label: 'Ventures', icon: Briefcase, Panel: VenturesPanel },
  { key: 'pillars', label: 'QNDS3 Pillars', icon: Target, Panel: PillarsPanel },
  { key: 'clusters', label: 'NDS3 Clusters', icon: Layers, Panel: ClustersPanel },
  { key: 'news', label: 'News Room', icon: Newspaper, Panel: NewsPanel },
  { key: 'media', label: 'Media (YouTube)', icon: Video, Panel: MediaPanel },
  { key: 'case', label: 'Case Studies', icon: FileText, Panel: CasePanel },
  { key: 'hero', label: 'Hero Images', icon: ImageIcon, Panel: HeroPanel },
  { key: 'inquiries', label: 'Inquiries', icon: MessageSquare, Panel: InquiriesPanel },
  { key: 'password', label: 'Change Password', icon: KeyRound, Panel: PasswordPanel },
];

export default function AdminDashboard() {
  const { user, token, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [tab, setTab] = useState('team');
  const cx = makeAdminClient(token);

  const onLogout = () => { logout(); navigate('/'); };
  const ActivePanel = TABS.find((t) => t.key === tab)?.Panel;

  return (
    <div data-testid="page-admin" className="min-h-screen bg-base">
      <Toaster theme={theme === 'dark' ? 'dark' : 'light'} position="top-right" richColors />
      <header className="border-b border-subtle surface-2 sticky top-0 z-30 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <Link to="/" data-testid="admin-home-logo" className="flex items-center gap-3 group" title="Back to MBK Holding">
            <img src={BRAND.logo} alt="MBK" className="h-10 w-auto transition-transform group-hover:scale-105" />
            <div>
              <p className="font-display text-lg text-prim tracking-tight">MBK Admin</p>
              <p className="text-xs text-muted font-mono">{user?.username} · {user?.role}</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sec hover:text-prim text-sm flex items-center gap-1.5"><ArrowLeft size={14} /> Back to site</Link>
            <button onClick={toggle} className="p-2 rounded-full border border-subtle text-sec hover:text-prim" title="Theme">
              {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="btn-secondary text-sm">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <nav className="surface-2 border border-subtle rounded-lg p-2 sticky top-24">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  data-testid={`admin-tab-${t.key}`}
                  onClick={() => setTab(t.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors ${
                    tab === t.key ? 'bg-brand-maroon text-white' : 'text-sec hover:bg-brand-teal/10 hover:text-prim'
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="lg:col-span-9 min-w-0">
          {ActivePanel && <ActivePanel cx={cx} />}
        </main>
      </div>
    </div>
  );
}
